/**
 * The beam out of the tip — the Luxor's sky beam, fired by a completion.
 *
 * The real thing is a fixed installation that never changes. This one is a
 * *readout*: every completed formation throws a column of light out of the apex,
 * and how hard it is thrown says what the move was worth. A lone three-bar gets a
 * short warm shaft; a nine-cell Great Pyramid at the end of a five-formation combo
 * gets a tall rainbow one that keeps burning through the next few announcements.
 * `beamShape` is the whole of that mapping and is deliberately the one piece of
 * this file with no three.js in it, so the numbers can be checked in a test.
 *
 * Three things fire together, and they are three different techniques:
 *
 *   1. **The column** (here). One camera-facing quad standing on the *base* of the
 *      pyramid and climbing off the top of the frame, with the beam drawn
 *      *inside* it by the fragment shader — falloff across, fade up, a travelling
 *      shimmer, and a rainbow that scrolls. Authoring it as a shader rather than
 *      as geometry is what lets the same mesh be a thin warm shaft and a wide
 *      spectral pillar without rebuilding anything. It stands on the base rather
 *      than on the tip so that its one hard edge is buried in the stonework —
 *      see BURIED.
 *   2. **The sparks** (`apex-beam.json`, spawned by `BoardVFX.apexBurst`). The
 *      shader cannot throw anything off the beam, and a column with nothing
 *      leaving it reads as a decal. Deliberately sparse and fine: they are there
 *      to say the column is throwing something off, and a fountain heavy enough
 *      to be looked at directly takes the eye off the beam it is supplementing.
 *   3. **The impact** (here, `shockOffset`). A ring of screen-space refraction
 *      pushed out from the tip in the post chain, which is the one part of this
 *      that touches pixels the beam never covers.
 *
 * ---------------------------------------------------------------------------
 * Why the column is HDR, and how far past 1 it goes
 * ---------------------------------------------------------------------------
 * The frame is bloomed at a 0.85 threshold and tone-mapped once at the end (see
 * `PyramidView.#buildPostProcessing`), so brightness above 1 is not clipping — it
 * is the *only* way to bloom. The core is authored at 2 and the halo at 1.3, and
 * the gap between them is the whole look: both clear the threshold, so the column
 * has real width to it rather than being a hairline with a glow painted on, but
 * the core is half again as bright and stays the white middle a spectrum can be
 * read against. Level the two up and the beam blooms flat and goes white.
 *
 * ---------------------------------------------------------------------------
 * Where it stands
 * ---------------------------------------------------------------------------
 * The column is a child of the pyramid group, standing on the board's own axis
 * from the base up through the apex, and it *tilts with the board*. It has to:
 * the buried third of it only stays buried if it shares the pyramid's frame. The
 * first version stood in the scene root, vertical in world space — and the moment
 * the board tilted toward the camera, the world-vertical line under the apex left
 * the solid and hung in the air in front of the stonework, bottom edge showing.
 * In the board's frame the segment from base to apex is inside the pyramid by
 * definition, at every angle the view allows.
 *
 * That framing is also what lets the pyramid occlude it. The material writes no
 * depth but still tests it, and the shell is opaque: the buried run of the quad
 * loses the depth test against the stonework and only the part past the tip
 * draws, so the beam is seen coming out of the top and nowhere else.
 *
 * The one degree of freedom left is the billboard yaw, about the board's own up —
 * never pitch or roll, which would lean the quad off the axis and bring the
 * buried part back out. Off-vertical when the board is tilted, and right: the
 * beam now reads as the pyramid's, not the sky's.
 */

import {
  AdditiveBlending,
  DoubleSide,
  Matrix4,
  Mesh,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Vector2,
  Vector3,
} from 'three/webgpu';
import { mix, smoothstep, uniform, uv, vec2, vec3, vec4 } from 'three/tsl';

import { APEX, BASE_VERTS } from './geometry.js';

const TWO_PI = Math.PI * 2;
const clamp01 = (v) => Math.min(1, Math.max(0, v));

/* ------------------------------------------------------------------- shape */

/**
 * The smallest and largest formation on the board, so a clear's size can be
 * normalised without this file knowing what the formations are: a 3x Bar is the
 * floor and the 9x Great Pyramid is the ceiling.
 */
const SMALLEST_CLEAR = 3;
const LARGEST_CLEAR = 9;
/** Combo size that maxes the display out — five formations is a huge move. */
const FULL_COMBO = 5;

/** How long the shortest and longest beams burn, in seconds. */
const MIN_SECONDS = 1.4;
const MAX_SECONDS = 4.3;

/**
 * What one completion is worth, as everything the display needs.
 *
 * Size and combo are deliberately weighted differently. Size is *what you built*
 * and moves the beam a little; combo is *what you pulled off* and moves it a lot,
 * because that is the rarer thing and the one the celebration is for. The rainbow
 * is combo alone — a single clear stays gold, the game's own colour for a score,
 * and the spectrum is held back for a move that earned it.
 *
 * @param {number} cells stones in the formation that just completed
 * @param {number} combo formations this move closed in total
 */
export function beamShape(cells, combo) {
  const size = clamp01((cells - SMALLEST_CLEAR) / (LARGEST_CLEAR - SMALLEST_CLEAR));
  const heat = clamp01((combo - 1) / (FULL_COMBO - 1));
  // Weighted so a combo beats a shape: three small bars in one move outshine a
  // lone Great Pyramid, and the two terms together reach exactly 1 at the top.
  const power = clamp01(0.28 + size * 0.22 + heat * 0.5);

  return {
    /** Peak brightness, and how the height and width scale with it. */
    power,
    /** Seconds the column burns for, attack to nothing. */
    duration: MIN_SECONDS + (MAX_SECONDS - MIN_SECONDS) * power,
    /** 0 = gold shaft, 1 = full spectrum up the column. */
    rainbow: clamp01(heat * 0.85 + size * 0.15),
    /** How hard the impact ring bends the frame, in screen widths. */
    shock: 0.004 + power * 0.014,
  };
}

/**
 * The envelope a fired beam follows, over `t` in 0..1 of its life.
 *
 * A near-instant attack and a long tail, because that is the order the event
 * happens in: the formation completes, *then* it burns off. Easing in over any
 * visible time would put the brightest moment after the thing it is announcing.
 *
 * The tail is barely curved — an exponent a shade over 1, so the decay is close
 * to linear. A steeper one dumps most of the brightness in the first fifth of the
 * life and leaves the rest of it a dim smear that is technically still burning
 * and reads as already over; near-linear is what makes a long beam actually look
 * long.
 */
export function beamEnvelope(t) {
  if (t <= 0 || t >= 1) return 0;
  const attack = smoothstepAt(0, 0.05, t);
  const decay = (1 - t) ** 1.1;
  return attack * decay;
}

/** Scalar smoothstep, for the envelope — the TSL one is a node. */
function smoothstepAt(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/* -------------------------------------------------------------------- look */

/**
 * How far below the tip the column actually starts.
 *
 * The quad has a bottom edge, and a bottom edge is a straight line where light
 * stops — which is exactly the thing a beam must not have. Standing it on the
 * tip put that line at the tip, in the open, at the one place the eye is already
 * looking. So it stands on the *base* instead and the whole lower third of it is
 * inside the pyramid, which is opaque and writes depth: the edge is occluded and
 * the column simply emerges from the stonework.
 *
 * Read off the geometry rather than written down, so it survives the board being
 * reshaped. A hair more than the full height, to be sure the edge is under the
 * base plane rather than exactly on it.
 */
const BURIED = APEX[1] - BASE_VERTS[0][1] + 0.1;

/**
 * Height of the column at rest and at full power, in board units — measured from
 * the base it stands on, so roughly `BURIED` of it is buried.
 */
const HEIGHT = [12, 20.5];
/** Width of the quad it is drawn in. The beam inside it is far narrower. */
const WIDTH = [1.3, 2.2];
/** How much wider the beam is at the top of the quad than at the tip. */
const SPREAD = 1.9;

/** Falloff exponents across the beam: the hot core, and the spill around it. */
const CORE_POWER = 10;
const HALO_POWER = 1.6;
/** Peak values of each. Only the core is meant to clear the bloom threshold. */
const CORE_GAIN = 2;
const HALO_GAIN = 1.3;
/** How fast the column dims with altitude, and where it fades out entirely. */
const CLIMB_FALLOFF = 0.75;
const CREST = [0.7, 1];

/** The colour a beam with no rainbow in it wears — the game's score gold. */
const WARM = [1, 0.84, 0.5];

/* ------------------------------------------------------------------- shock */

/** Seconds the impact ring takes to cross the frame, and how far it gets. */
const SHOCK_SECONDS = 0.55;
const SHOCK_REACH = 0.85;
/** Thickness of the refracting band, in screen widths. */
const SHOCK_WIDTH = 0.11;

export class ApexBeam {
  /**
   * @param {import('three/webgpu').Object3D} board the pyramid group. The beam is
   *   a child of it and turns with it, which is what keeps it inside the solid —
   *   see the header.
   */
  constructor(board) {
    this.board = board;

    /** Live envelope: how strong this firing is, and where it is in its life. */
    this.power = 0;
    this.age = 0;
    this.life = 0;
    this.rainbow = 0;
    /** Advances forever; drives the shimmer and the hue scroll. */
    this.clock = 0;

    /** The impact ring's own clock, which outlives nothing — it is half a second. */
    this.shockAge = SHOCK_SECONDS;
    this.shockPeak = 0;

    this.uIntensity = uniform(0);
    this.uTime = uniform(0);
    this.uRainbow = uniform(0);
    /** Rolled per firing, so two beams in a row are not the same rainbow. */
    this.uHue = uniform(0);

    this.uOrigin = uniform(new Vector3());
    this.uViewProjection = uniform(new Matrix4());
    /** (aspect, 1): the frame is not square, and a ring has to be. */
    this.uAspect = uniform(new Vector2(1, 1));
    this.uShockRadius = uniform(0);
    this.uShockAmp = uniform(0);

    // Standing on its own bottom edge, so scaling y grows it upward out of the
    // tip rather than about its middle.
    const geometry = new PlaneGeometry(1, 1);
    geometry.translate(0, 0.5, 0);

    this.mesh = new Mesh(geometry, this.#material());
    this.mesh.visible = false;
    // Over the stonework and over the particles: it is the brightest thing on
    // screen while it burns, and it writes no depth for anything to sort against.
    this.mesh.renderOrder = 5;
    this.mesh.frustumCulled = false;
    // Set once and never again: in the board's own frame the foot of the beam is
    // a fixed point under the apex, and the board's transform does the rest.
    this.mesh.position.set(APEX[0], APEX[1] - BURIED, APEX[2]);
    board.add(this.mesh);

    this.scratch = new Vector3();
    /** The tip in world space, for aiming the impact ring. */
    this.tip = new Vector3();
  }

  /**
   * The column, drawn inside its quad.
   *
   * All of it is a function of the quad's own uv — `x` across, `y` up from the
   * tip — so the mesh is one unit square and every dimension the beam has comes
   * from the scale it is given and the uniforms it is drawn with.
   */
  #material() {
    const coord = uv();
    const height = coord.y;

    // Across: normalised to a beam that widens with altitude, which is what
    // stops a tall firing reading as a flat strip of tape.
    const spread = mix(1, SPREAD, height);
    const across = coord.x.sub(0.5).mul(2).div(spread).abs().clamp(0, 1);
    const core = across.oneMinus().pow(CORE_POWER);
    const halo = across.oneMinus().pow(HALO_POWER);

    // Up: dimming with altitude and cut off softly short of the top edge, so
    // the beam ends in air rather than at the boundary of its own geometry.
    const column = height
      .oneMinus()
      .pow(CLIMB_FALLOFF)
      .mul(smoothstep(CREST[0], CREST[1], height).oneMinus());

    // Two travelling bands, one slow and wide, one fast and fine. Together they
    // read as light with something moving through it rather than as a gradient.
    const shimmer = height
      .mul(9)
      .sub(this.uTime.mul(2.2))
      .sin()
      .mul(0.12)
      .add(height.mul(41).sub(this.uTime.mul(7)).sin().mul(0.06))
      .add(1);

    // Cosine wheel: three cosines a third of a turn apart is a full spectrum in
    // three instructions, and it is continuous, so the scroll never seams.
    const phase = height.mul(0.85).sub(this.uTime.mul(0.22)).add(this.uHue);
    const wheel = vec3(phase, phase.add(1 / 3), phase.add(2 / 3))
      .mul(TWO_PI)
      .cos()
      .mul(0.5)
      .add(0.5);
    const tint = mix(vec3(...WARM), wheel, this.uRainbow);

    // The core stays near-white whatever the rainbow is doing: a spectrum is
    // only legible against something that is not one, and the white middle is
    // what makes the colour read as spill off a beam.
    const rgb = tint.mul(halo.mul(HALO_GAIN)).add(vec3(1, 0.97, 0.92).mul(core.mul(CORE_GAIN)));

    const material = new MeshBasicNodeMaterial({
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });
    material.colorNode = rgb.mul(column).mul(shimmer).mul(this.uIntensity);
    material.opacityNode = core.add(halo.mul(0.75)).clamp(0, 1).mul(column);
    return material;
  }

  /* ---------------------------------------------------------------- firing */

  /**
   * Throw a beam. Louder than the one burning, if there is one.
   *
   * A combo announces its formations one after another, so this is re-entered
   * every few hundred milliseconds with a rising count — and a beam that simply
   * restarted would get *shorter* each time the move got better, because a fresh
   * envelope drops it back to nothing before climbing. Taking the max of the two
   * powers and of the two remaining lives instead means a combo mounts.
   *
   * @param {{cells: number, combo: number}} move
   */
  fire({ cells, combo }) {
    const shape = beamShape(cells, combo);
    const remaining = Math.max(0, this.life - this.age);

    this.power = Math.max(this.power * this.#envelope(), shape.power);
    this.rainbow = Math.max(this.rainbow, shape.rainbow);
    this.life = Math.max(remaining, shape.duration);
    this.age = 0;
    this.uHue.value = Math.random();

    // The impact restarts outright rather than taking a max: it is an *impact*,
    // and two of them a beat apart should read as two.
    this.shockAge = 0;
    this.shockPeak = shape.shock;
    return shape;
  }

  #envelope() {
    return this.life > 0 ? beamEnvelope(this.age / this.life) : 0;
  }

  /**
   * Turn the beam to face the camera, and advance both clocks.
   *
   * Where it *stands* is not touched here — in the board's frame that is a fixed
   * point, set in the constructor, and the board's own transform carries it.
   *
   * @param {number} dt seconds since the last frame
   * @param {import('three/webgpu').Camera} camera
   */
  update(dt, camera) {
    this.clock += dt;
    this.uTime.value = this.clock;

    if (this.shockAge < SHOCK_SECONDS) {
      this.shockAge = Math.min(SHOCK_SECONDS, this.shockAge + dt);
      const t = this.shockAge / SHOCK_SECONDS;
      // Out fast and slowing, fading as it goes — a wave losing energy, and the
      // fade is what keeps it from stopping dead at the edge of its reach.
      this.uShockRadius.value = SHOCK_REACH * (1 - (1 - t) ** 2);
      this.uShockAmp.value = this.shockPeak * (1 - t) ** 2;
    } else {
      this.uShockAmp.value = 0;
    }

    // The ring is measured from the tip whether or not the column is up, so a
    // beam that has burnt out still leaves its impact travelling. The tip is
    // walked into world space here rather than handed in: the beam already knows
    // where it lives.
    this.board.updateWorldMatrix(true, false);
    this.uOrigin.value.copy(this.board.localToWorld(this.tip.set(...APEX)));
    this.uViewProjection.value.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse,
    );

    if (this.life <= 0) {
      this.mesh.visible = false;
      return;
    }

    this.age += dt;
    const envelope = this.#envelope();
    const intensity = this.power * envelope;
    if (this.age >= this.life || intensity <= 0.001) {
      this.life = 0;
      this.power = 0;
      this.rainbow = 0;
      this.uIntensity.value = 0;
      this.mesh.visible = false;
      return;
    }

    this.uIntensity.value = intensity;
    this.uRainbow.value = this.rainbow;

    // Yawed about the board's own up to face the camera — never pitched or
    // rolled, because a quad that leans to face the lens leaves the axis of the
    // pyramid, and the buried part of it comes back out of the stonework. The
    // camera is pulled into board space so the yaw happens in the frame the mesh
    // actually lives in.
    this.board.worldToLocal(this.scratch.copy(camera.position)).sub(this.mesh.position);
    this.mesh.rotation.set(0, Math.atan2(this.scratch.x, this.scratch.z), 0);

    // Height and width both ride the envelope, so it shoots out and draws back
    // in rather than fading in place.
    const grown = 0.55 + 0.45 * envelope;
    this.mesh.scale.set(
      (WIDTH[0] + (WIDTH[1] - WIDTH[0]) * this.power) * (0.7 + 0.3 * grown),
      (HEIGHT[0] + (HEIGHT[1] - HEIGHT[0]) * this.power) * grown,
      1,
    );
    this.mesh.visible = true;
  }

  /* ------------------------------------------------------------ distortion */

  /**
   * The screen-space offset the impact ring bends `base` by — a ring of
   * refraction expanding from the tip, and zero everywhere else.
   *
   * The centre is worked out here rather than handed in as a screen position,
   * because the pass is composited on a quad whose camera is not the scene's:
   * the apex arrives as a world point and its own view-projection, and is
   * projected in the shader. `uv()` runs y-down on that quad, hence the flip.
   *
   * @param {import('three/tsl').Node} base the uv being sampled
   */
  shockOffset(base) {
    const clip = this.uViewProjection.mul(vec4(this.uOrigin, 1));
    const w = clip.w.max(0.0001);
    const centre = vec2(clip.x.div(w).mul(0.5).add(0.5), clip.y.div(w).mul(-0.5).add(0.5));

    // Distances are measured in screen *widths*, so the ring is round on a wide
    // viewport instead of an ellipse; the offset is unwound again before use.
    const delta = base.sub(centre).mul(this.uAspect);
    const distance = delta.length().max(0.0001);
    const band = distance.sub(this.uShockRadius).div(SHOCK_WIDTH);
    const ring = band.mul(band).negate().exp();

    return delta.div(distance).div(this.uAspect).mul(ring.mul(this.uShockAmp));
  }

  /** The frame changed shape; the ring has to stay round. */
  resize(width, height) {
    this.uAspect.value.set(width / Math.max(1, height), 1);
  }

  /** Put it out, for a restart. */
  reset() {
    this.power = 0;
    this.life = 0;
    this.age = 0;
    this.rainbow = 0;
    this.shockAge = SHOCK_SECONDS;
    this.uIntensity.value = 0;
    this.uShockAmp.value = 0;
    this.mesh.visible = false;
  }
}
